<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref, watch, nextTick} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useWindowSize} from "@vueuse/core";
import {useI18n} from "vue-i18n";
import { useRouteHelpers } from "@/composables/useRouteHelp";
import { useUserStore } from "@/stores/userStore";

import Drawer from "@/components/MenuDrawer/MenuDrawer.vue";
import HeaderBookingSearch from "@/components/BookingComponent/HeaderBookingSearch.vue";
import {throttle} from "lodash";
import {useComparisonStore} from "@/stores/comparisonStore";
import Localization from "@/components/GlobalHeader/Localization.vue";
import UserInfo from "@/components/UserInfo/UserInfo.vue";
import { useAuth0 } from "@auth0/auth0-vue";
import defaultAvatar from '@/assets/icons/svg/DefaultAvatar.svg';
import {useHomepageStore} from "@/stores/homepageStore";

const { loginWithRedirect } = useAuth0();
const loginEnabled = import.meta.env.VITE_LOGIN_ENABLED


// Stores and Utilities
const comparisonStore = useComparisonStore();
const { t } = useI18n();
const { isHomePage, isYachtsOrSearchPage, isAboutPage, isYachtsPage,isRecommendationsPage, isSearchPage, isSavedOfferPage } = useRouteHelpers();

// Vue Router
const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const homePageStore = useHomepageStore();

// Window Size
const { width } = useWindowSize();
const isMobile = computed(() => width.value <= 1920);
const isMobileSmall = computed(() => width.value <= 768);

// State
const isDrawerOpen = ref(false);
const loginHover = ref(false);
const activeSection = ref("");
const isScrolled = ref(false);

const currentIndex = ref(0);
let interval:any = null;
const homepageStore = useHomepageStore();


// Methods
const openDrawer = () => (isDrawerOpen.value = true);
const closeDrawer = () => (isDrawerOpen.value = false);

const scrollToElement = (el, offset = 0) => {
  const element = document.getElementById(el);
  if (element) {
    const yOffset = offset;
    const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

function navigateToComparison() {
  router.push({
    path: '/yachts/compare',
    query: {
      ids: comparisonStore.yachtsToCompare.map(theBoat => theBoat.yacht.slug).join(','),
      locationIds: route.query.locationIds,
      countryIds: route.query.countryIds,
      cityIds: route.query.cityIds,
      regionIds: route.query.regionIds,
      dateFrom: route.query.dateFrom,
      dateTo: route.query.dateTo,
      cabins: route.query.cabins
    },
  });
}

const throttledHandleScroll = throttle(() => {
  isScrolled.value = window.scrollY > 75;
}, 250);

// Observe Sections
const observeSections = () => {
  const sections = document.querySelectorAll("section");
  const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            activeSection.value = entry.target.id;
          }
        });
      },
      { threshold: 0.5 }
  );

  sections.forEach((section) => observer.observe(section));
  return () => observer.disconnect(); // Cleanup observer
};


onUnmounted(() => {
  clearInterval(interval);
});

onMounted(() => {
  homePageStore.getHeaderAnnouncement();
  interval = setInterval(() => {
    if (!homepageStore.headerAnnouncements.length) return;

    currentIndex.value =
        (currentIndex.value + 1) %
        homepageStore.headerAnnouncements.length;

  }, 4000);
  
  throttledHandleScroll();
  const cleanupObserver = observeSections();
  window.addEventListener("scroll", throttledHandleScroll);

  const waitForElement = (id, callback) => {
    const element = document.getElementById(id);
    if (element) {
      callback();
    } else {
      setTimeout(() => waitForElement(id, callback), 100);
    }
  };

  // Delayed scroll on reload
  setTimeout(() => {
    requestAnimationFrame(() => {
      if (route.path === "/about") {
        scrollToElement("experience-section", -100);
      } else if (route.path.startsWith("/yachts")) {
        waitForElement("overview", () => {
          return
        });
      }
      observeSections(); // Restart observer
    });
  }, 500);

  onUnmounted(() => {
    cleanupObserver();
    window.removeEventListener("scroll", throttledHandleScroll);
  });
});

// Watch Route Change for Smooth Scrolling on Navigation
watch(
    () => route.path,
    async (newPath) => {
      await nextTick(); // Ensure DOM is updated before scrolling
      setTimeout(() => {
        if (newPath === "/about") {
          scrollToElement("experience-section", -100);
        } else if (newPath.startsWith("/yachts")) {
          scrollToElement("overview", 0);
        }
        observeSections(); // Restart observer
      }, 300);
    }
);

</script>

<template>
  <div
      :class="`header flex items-center overflow-visible ${
    isScrolled ? 'scrolled' : ''
  } ${
    isHomePage
      ? 'absolute w-full header-light border-b-[1px]'
      : 'bg-white sticky border-b-[1px] border-bw-grey'
  } top-0 z-10 sm:z-[100]`"
  >
    <div
        :class="`w-screen ${
        isYachtsOrSearchPage ? 'top-0 fixed bg-white z-200 border-b' : 'sticky z-50'
      }`"
    >
      <div class="container flex flex-row justify-between items-center h-18 mx-auto">
        <!-- Logo Section -->
        <div class="flex items-center gap-x-6 flex-shrink-0">
          <router-link :to="`/${$i18n.locale}`" class="logo-link">
            <img
                src="@/assets/images/cosmos_logo_black.png"
                class="cursor-pointer h-8 logo"
                alt="Cosmos Logo"
            />
          </router-link>
        </div>
        <!-- Conditional Buttons for About Page -->
        <div
            v-if="isHomePage"
            class="container flex flex-grow justify-center items-center gap-4 pr-18 w-full md:flex"
        >
          <transition name="fade" mode="out-in">
            <div
                v-if="homepageStore.headerAnnouncements.length"
                :key="currentIndex"
                class="hidden sm:block w-auto ml-3 mt-4 text-white text-center headerAnnouncement"
                v-html="homepageStore.headerAnnouncements[currentIndex].content"
            ></div>
          </transition>
        </div>
        <div v-if="isAboutPage" class="container flex gap-4 justify-start w-full ml-13 hidden md:flex">
          <!-- Dynamic Button Classes Based on Active Section -->
          <div
              @click="scrollToElement('experience-section', -100)"
              :class="[
                'responsive-body-small border-2 py-0.5 px-4 rounded-full cursor-pointer transition-[border-color]',
                activeSection === 'experience-section' ? 'border-black' : 'hover:border-black'
              ]"
          >
            {{ t('drawer.whyCosmos') }}
          </div>
          <div
              @click="scrollToElement('milestone-section', -100)"
              :class="[
                'responsive-body-small border-2 py-0.5 px-4 rounded-full cursor-pointer transition-[border-color]',
                activeSection === 'milestone-section' ? 'border-black' : 'hover:border-black'
              ]"
          >
            {{ t('drawer.experience') }}
          </div>
          <div
              @click="scrollToElement('at-a-glance', -100)"
              :class="[
                'responsive-body-small border-2 py-0.5 px-4 rounded-full cursor-pointer transition-[border-color]',
                activeSection === 'at-a-glance' ? 'border-black' : 'hover:border-black '
              ]"
          >
            {{ t('drawer.glance') }}
          </div>
        </div>



        <!-- Conditional Buttons for Yacht Page -->
        <div
            class="yacht-page-header__menu justify-start w-full ml-13 gap-x-2 hidden"
            v-if="isYachtsPage || isSavedOfferPage"
        >
          <div
              @click="scrollToElement('overview')"
              :class="[
              'responsive-body-small font-semibold border-2 py-0.5 px-4 rounded-full cursor-pointer transition-[border-color]',
               activeSection === 'overview' ? 'border-black' : 'hover:border-black'
             ]"
          >
            {{ t('header.menu.overview') }}
          </div>

          <div
              @click="scrollToElement('gallery', -50)"
              :class="[
              'responsive-body-small font-semibold border-2 py-0.5 px-4 rounded-full cursor-pointer transition-[border-color]',
              activeSection === 'gallery' ? 'border-black' : 'hover:border-black'
            ]"
          >
            {{ t('header.menu.gallery') }}
          </div>

          <div
              @click="scrollToElement('addons', -80)"
              :class="[
              'responsive-body-small font-semibold border-2 py-0.5 px-4 rounded-full cursor-pointer transition-[border-color]',
              activeSection === 'addons' ? 'border-black' : 'hover:border-black'
            ]"
          >
            {{ t('header.menu.addons') }}
          </div>

          <div
              @click="scrollToElement('things-to', -150)"
              :class="[
              'responsive-body-small font-semibold border-2 py-0.5 px-4 rounded-full cursor-pointer transition-[border-color]',
              activeSection === 'things-to' ? 'border-black' : 'hover:border-black'
            ]"
          >
            {{ t('header.menu.thingsToKnow') }}
          </div>
        </div>

        <!-- Booking Search Section -->
        <div
            v-if="isRecommendationsPage && !isMobile"
            class="header-book-search responsive-body-small flex-grow flex justify-center mx-4"
        >
          <HeaderBookingSearch />
        </div>

        <!-- Right Section -->
        <!-- Language Switcher --->
        <div class="flex items-center gap-3 md:gap-6 flex-shrink-0">
          <Localization />
          <!-- Comparison Button -->
          <button
              v-if="isSearchPage || isRecommendationsPage"
              :class="`relative border px-3 py-1.5 transition-all ${
            comparisonStore.yachtsToCompare.length <= 1
              ? 'border-gray cursor-not-allowed'
              : 'border-primary-blue cursor-pointer'
          } rounded-12xl flex items-center gap-2`"
              :disabled="comparisonStore.yachtsToCompare.length <= 0"
              @click="
            comparisonStore.yachtsToCompare.length > 1 &&
            navigateToComparison()
          "
          >
            <img
                class="transition-transform duration-300"
                :class="comparisonStore.yachtsToCompare.length > 1 ? 'opacity-100 filter' : 'opacity-40'"
                src="@/assets/icons/svg/Comparison_16x16.svg"
                alt="comparison"
            />
            <div class="notification-bubble flex items-end justify-center">
              {{ comparisonStore.yachtsToCompare.length }}
            </div>
          </button>
          <!--    ENABLE FOR LOGIN-->

                    <div v-if="userStore.isLoggedIn">
                      <div class="flex items-center gap-2 cursor-pointer relative"
                           @mouseover="loginHover = true"
                           @mouseleave="loginHover = false"
                           @click="isMobileSmall && openDrawer()"
                      >
                        <transition name="popup">
                          <div v-if="loginHover"  class="pt-10 w-full absolute z-50 top-0 left-0">
                            <div class="flex flex-col absolute z-10 p-3 bg-white rounded-6xl responsive-body-medium drop-shadow-popup text-no-wrap">
                              <router-link to="/my/trips"
                                           @click="loginHover = false"
                                           class="flex gap-x-1 px-2 py-2 rounded-6xl hover:bg-[#EEEEF0] transition-[background]">
                                <img src="@/assets/icons/svg/Luggage_24x24.svg" width="16" alt="My trips">
                                {{ t('globalHeader.trips') }}
                              </router-link>

                              <router-link to="/my/crew-list"
                                           @click="loginHover = false"
                                           class="flex gap-x-1 pr-5 pl-2 py-2 rounded-6xl hover:bg-[#EEEEF0] transition-[background]">
                                <img src="@/assets/icons/svg/Crew_24x24.svg" width="16" alt="My crew">
                                {{ t('globalHeader.crewList') }}
                              </router-link>
                              
                              <router-link to="/my/account"
                                           @click="loginHover = false"
                                           class="flex gap-x-1 align-center pr-5 pl-2 py-2 rounded-6xl hover:bg-[#EEEEF0] transition-[background]">
                                <img src="@/assets/icons/svg/Guest_16x16.svg" width="16" alt="My account">

                                {{ t('globalHeader.account') }}
                              </router-link>
                              <div
                                  @click="userStore.logout()"
                                  class="flex gap-x-1 align-center px-2 py-2 rounded-6xl hover:bg-[#EEEEF0] transition-[background]">
                                <img src="@/assets/icons/svg/Log out_16x16.svg" width="16" alt="Log out">

                                {{ t('globalHeader.logout') }}
                              </div>
                            </div>
                          </div>
                        </transition>
                        <div v-if="!isMobileSmall && !userStore.isLoading" class="flex align-center space-x-2">
                          <UserInfo
                              :member="userStore.user"
                              class="login"
                          />
                        </div>
                      </div>

                    </div>

                    <div v-else>
                      <div v-if="!isMobileSmall && loginEnabled === 'true'"
                          :class="[
                            'font-semibold flex items-center hover:underline cursor-pointer responsive-body-small h-[30px]',
                            isHomePage ? 'text-white' : 'text-black'
                          ]"
                           @click="loginWithRedirect()"
                      > <!--@click="$router.push('/login')"-->
                        {{ t('header.login') }}
                      </div>
                    </div>

          <!-- Separation Line --->
          <div
              :class="`font-semibold flex align-center hover:underline responsive-body-small h-7.5 pr-0
                ${ isHomePage ? 'border-r border-[#ffffff]' : 'border-e'}`"
          ></div>
          <!-- Drawer Menu -->
          <div
              :class="`border w-12 h-7 flex justify-center items-center rounded-11xl cursor-pointer transition-all ${
                isHomePage ? 'border-white' : 'border-black'
              }`"
              @click="openDrawer"
          >
            <img class="drawer-menu__img" src="@/assets/icons/svg/Menu_16x16.svg" />
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
      v-if="isRecommendationsPage && isMobile"
      class="header-book-search responsive-body-small font-semibold sticky top-18 z-50 bg-bw-white"
  >
    <HeaderBookingSearch />
  </div>

  <Drawer :isOpen="isDrawerOpen" @close="closeDrawer"/>

</template>

<style scoped>

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.6s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.headerAnnouncement ::v-deep(p) {
  margin-bottom: 0 !important;
}

.header-light .language-switch__img,
.header-light .drawer-menu__img {
  filter: contrast(0%) brightness(2);
}

.logo {
  filter: brightness(70%) contrast(1000%);
}

.header-light .logo {
  filter: brightness(0) invert(1) !important;
}

.header-light .login {
  color: #fff;
  border-color: #fff !important;
}

.header-light .drawer-menu {
  border-color: #fff !important;
}


.comparison-icons-selected {
  filter: invert(1);
}

.notification-bubble {
  background-color: #000000; /* Bubble background color */
  color: white; /* Text color */
  font-size: 11px; /* Text size */
  width: 20px; /* Width of the bubble */
  height: 20px; /* Height of the bubble */
  border-radius: 50%; /* Make the bubble round */
  position: absolute; /* Positioning relative to the parent */
  right: 0px; /* Distance from the right edge of the parent */
  top: -12px; /* Positioning the bubble slightly above the top edge */
}

@media (min-width: 1024px) {
  .yacht-page-header__menu {
    display: flex;
  }
}

@media (max-width: 768px) {
  .logo {
    height: 24px;
    width: auto;
  }

  .rightFlex {
    gap: 10px;
  }

}


@keyframes slide-opacity {
  0% {
    transform: translateY(0);
    opacity: 1;
  }

  100% {
    transform: translateY(-30px);
    opacity: 0;
  }
}

.border-white {
  border-width: 0.5px !important; /* Applies to all sides */
  border-color: white !important; /* Sets the border color to white */
}
</style>
